import { FormLeavingListener } from '@exam-center/components';
import {
  useGetAllProvincesQuery,
  useGetBuildingsByLocationIdQuery,
  useGetLocationsByProvinceIdQuery,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { Form, type OptionSelectionProps } from '@shared/components';
import { usePermission, validatePermissions } from '@shared/permissions';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  type PropsWithChildren,
} from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';

import {
  ExamLocationRequestFormSchema,
  type ExamLocationRequestFormInputs,
} from '../../containers/exam-location-request-form/schema';

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

/**
 * UI state kept in RHF form. Selection fields use OptionSelectionProps to work
 * with shared <FormSearchSelection /> component.
 */
export interface ExamLocationRequestFormState {
  provinceId: OptionSelectionProps | null;
  locationId: OptionSelectionProps | null;
  locationNameTh: string | null;
  locationNameEn: string | null;
  buildingId: OptionSelectionProps | null;
  buildingName: string | null;
}

/**
 * Parsed / validated submit payload (API shape).
 */
export type ExamLocationRequestFormSubmitInput = ExamLocationRequestFormInputs;

/**
 * Options that can be passed to the submit function
 */
export interface ExamLocationRequestFormSubmitOptions {
  showSuccessToast?: boolean;
}

/**
 * Provider props.
 * onSubmit receives the parsed schema values (already validated).
 * initialValues expect API shape (NOT OptionSelection objects).
 */
export interface ExamLocationRequestFormProviderProps extends PropsWithChildren {
  onSubmit: (
    input: ExamLocationRequestFormSubmitInput,
    options?: ExamLocationRequestFormSubmitOptions,
  ) => void | Promise<void>;
  /**
   * If provided, the form will be pre-populated (update mode).
   * All ids should be valid UUID strings as per schema.
   */
  initialValues?: Partial<ExamLocationRequestFormSubmitInput>;
  /**
   * Whether to render a <form> element inside the provider.
   * Set to false if you want to place the <Form> wrapper at a higher level.
   * Default: true
   */
  withFormElement?: boolean;
  /**
   * Label for submit button (consumer can ignore if rendering custom actions).
   * Not used internally; just kept for potential future extension.
   */
  submitLabel?: string;
}

/* -------------------------------------------------------------------------------------------------
 * Internal Context
 * -----------------------------------------------------------------------------------------------*/

interface LocationOption extends OptionSelectionProps {
  nameEn: string | null;
}

interface InternalContextValue {
  form: UseFormReturn<ExamLocationRequestFormState>;
  locationMode: 'existing' | 'new' | '';
  setLocationMode: React.Dispatch<React.SetStateAction<'existing' | 'new' | ''>>;
  buildingMode: 'existing' | 'new' | '';
  setBuildingMode: React.Dispatch<React.SetStateAction<'existing' | 'new' | ''>>;
  provinceOptions: OptionSelectionProps[];
  locationOptions: LocationOption[];
  buildingOptions: OptionSelectionProps[];
  isProvinceSelected: boolean;
  isLocationSelected: boolean;
  submitting: boolean;
  /**
   * True when provider received initialValues (update mode), false for creation mode.
   * Consumers (e.g. form UI) can use this to decide where to render submit actions.
   */
  isUpdateMode: boolean;
  /**
   * Triggers form submit programmatically
   */
  submit: (options?: ExamLocationRequestFormSubmitOptions) => Promise<void>;
  /**
   * True when all required fields for the selected mode are filled
   */
  canSubmit: boolean;
}

const ExamLocationRequestFormInternalContext = createContext<InternalContextValue | undefined>(
  undefined,
);

export function useExamLocationRequestForm() {
  const ctx = useContext(ExamLocationRequestFormInternalContext);
  if (!ctx) {
    throw new Error(
      'useExamLocationRequestForm must be used inside <ExamLocationRequestFormProvider />',
    );
  }
  return ctx;
}

/* -------------------------------------------------------------------------------------------------
 * Provider
 * -----------------------------------------------------------------------------------------------*/

export default function ExamLocationRequestFormProvider(
  props: Readonly<ExamLocationRequestFormProviderProps>,
) {
  const { children, onSubmit, initialValues, withFormElement = true } = props;

  const { permissions } = usePermission();
  const hasUpdatePermission = validatePermissions(
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
    permissions,
  );

  // Local UI modes
  const [locationMode, setLocationMode] = useState<'existing' | 'new' | ''>(() => {
    if (initialValues?.locationId || initialValues?.locationNameTh) {
      // Decide mode from initial values (if both names => custom/new, else existing)
      return initialValues.locationId ? 'existing' : 'new';
    }
    return '';
  });
  const [buildingMode, setBuildingMode] = useState<'existing' | 'new' | ''>(() => {
    if (initialValues?.buildingId || initialValues?.buildingName) {
      return initialValues.buildingId ? 'existing' : 'new';
    }
    return 'existing';
  });

  // Build RHF form with a custom resolver that adapts OptionSelectionProps -> schema shape
  const form = useForm<ExamLocationRequestFormState>({
    defaultValues: {
      provinceId: initialValues?.provinceId ? { label: '', value: initialValues.provinceId } : null,
      locationId: initialValues?.locationId ? { label: '', value: initialValues.locationId } : null,
      locationNameTh: initialValues?.locationNameTh ?? null,
      locationNameEn: initialValues?.locationNameEn ?? null,
      buildingId: initialValues?.buildingId ? { label: '', value: initialValues.buildingId } : null,
      buildingName: initialValues?.buildingName ?? null,
    },
    resolver: async (values) => {
      const transformed: ExamLocationRequestFormSubmitInput = {
        provinceId: (values.provinceId?.value as string) ?? '',
        locationId: (values.locationId?.value as string) ?? null,
        locationNameTh: values.locationNameTh ?? null,
        locationNameEn: values.locationNameEn ?? null,
        buildingId: (values.buildingId?.value as string) ?? null,
        buildingName: values.buildingName ?? null,
      };

      const parsed = ExamLocationRequestFormSchema.safeParse(transformed);
      if (parsed.success) {
        return { values, errors: {} };
      }

      const errors: Record<string, { type: string; message: string }> = {};
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];
        if (
          field &&
          [
            'provinceId',
            'locationId',
            'locationNameTh',
            'locationNameEn',
            'buildingId',
            'buildingName',
          ].includes(field as string)
        ) {
          errors[field as string] = {
            type: issue.code,
            message: issue.message,
          };
        }
      });
      return { values: {}, errors };
    },
  });

  // Watch province & location for dependent behavior
  const selectedProvince = form.watch('provinceId');
  const provinceId = selectedProvince?.value ?? '';
  const selectedLocation = form.watch('locationId');

  // Also watch fields used for enabling submit
  const locationNameTh = form.watch('locationNameTh');
  const buildingIdSelected = form.watch('buildingId');
  const buildingName = form.watch('buildingName');

  // Queries
  const { data: ecGetAllProvincesData, loading: loadingProvinces } = useGetAllProvincesQuery();

  const { data: ecGetLocationsByProvinceId, loading: loadingLocations } =
    useGetLocationsByProvinceIdQuery({
      skip: !provinceId,
      variables: {
        getLocationsByProvinceIdInput: {
          provinceId: typeof provinceId === 'string' ? provinceId : '',
        },
      },
    });

  const { data: ecGetBuildingsByLocationId, loading: loadingBuildings } =
    useGetBuildingsByLocationIdQuery({
      skip: !selectedLocation?.value,
      variables: {
        getBuildingsByLocationIdInput: {
          locationId: (selectedLocation?.value as string) ?? '',
        },
      },
    });

  // Option lists
  const provinceOptions = useMemo<OptionSelectionProps[]>(() => {
    return (
      ecGetAllProvincesData?.ecGetAllProvinces?.provinces.map((province) => ({
        label: province.nameTh,
        value: province.id,
      })) ?? []
    );
  }, [ecGetAllProvincesData]);

  const locationOptions = useMemo<LocationOption[]>(() => {
    return (
      ecGetLocationsByProvinceId?.ecGetLocationsByProvinceId?.locations.map((location) => ({
        label: location.nameTh,
        nameEn: location.nameEn ?? null,
        value: location.id,
      })) ?? []
    );
  }, [ecGetLocationsByProvinceId]);

  const buildingOptions = useMemo<OptionSelectionProps[]>(() => {
    return (
      ecGetBuildingsByLocationId?.ecGetBuildingsByLocationId?.buildings
        .map((building) => ({
          label: building.nameTh,
          value: building.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [ecGetBuildingsByLocationId]);

  const isProvinceSelected = !!form.watch('provinceId');
  const isLocationSelected = !!selectedLocation;

  // Hydrate initial values after options load and whenever initialValues change
  const previousInitialValuesRef = useRef<typeof initialValues>();
  useEffect(() => {
    const hasInitials = !!(
      initialValues &&
      Object.values(initialValues).some((v) => v !== undefined && v !== null && v !== '')
    );

    if (!hasInitials) return;

    // Check if initialValues actually changed
    const initialValuesChanged =
      JSON.stringify(previousInitialValuesRef.current) !== JSON.stringify(initialValues);

    if (!initialValuesChanged && previousInitialValuesRef.current) return;

    const needsProvince = !!initialValues?.provinceId;
    const needsLocation = !!initialValues?.locationId;
    const needsBuilding = !!initialValues?.buildingId;

    // Wait until the necessary options are available for proper label hydration
    if (
      (needsProvince && provinceOptions.length === 0) ||
      (needsLocation && locationOptions.length === 0) ||
      (needsBuilding && buildingOptions.length === 0)
    ) {
      return;
    }

    const current = form.getValues();

    const next: ExamLocationRequestFormState = {
      ...current,
      provinceId: current.provinceId,
      locationId: current.locationId,
      locationNameTh: initialValues?.locationNameTh ?? null,
      locationNameEn: initialValues?.locationNameEn ?? null,
      buildingId: current.buildingId,
      buildingName: initialValues?.buildingName ?? null,
    };

    if (needsProvince) {
      const provinceMatch = provinceOptions.find((o) => o.value === initialValues?.provinceId);
      if (provinceMatch) {
        next.provinceId = provinceMatch;
      }
    }

    if (needsLocation) {
      const locationMatch = locationOptions.find((o) => o.value === initialValues?.locationId);
      if (locationMatch) {
        next.locationId = locationMatch;
      }
    }

    if (needsBuilding) {
      const buildingMatch = buildingOptions.find((o) => o.value === initialValues?.buildingId);
      if (buildingMatch) {
        next.buildingId = buildingMatch;
      }
    }

    // Update modes based on new initial values
    if (initialValues?.locationId || initialValues?.locationNameTh) {
      setLocationMode(initialValues.locationId ? 'existing' : 'new');
    }
    if (initialValues?.buildingId || initialValues?.buildingName) {
      setBuildingMode(initialValues.buildingId ? 'existing' : 'new');
    }

    form.reset(next, { keepDirty: false, keepTouched: false });
    previousInitialValuesRef.current = initialValues;
  }, [initialValues, provinceOptions, locationOptions, form, buildingOptions]);

  // Province change -> reset dependent fields & modes
  useEffect(() => {
    // Avoid wiping out initial values on the very first mount if they correspond to existing province
    if (!provinceId) {
      form.setValue('locationId', null);
      form.setValue('locationNameTh', null);
      form.setValue('locationNameEn', null);
      form.setValue('buildingId', null);
      form.setValue('buildingName', null);
      setLocationMode('');
      setBuildingMode('existing');
      form.clearErrors([
        'locationId',
        'locationNameTh',
        'locationNameEn',
        'buildingId',
        'buildingName',
      ]);
      return;
    }

    // If user changes province away from initial one
    if (provinceId !== initialValues?.provinceId) {
      form.setValue('locationId', null);
      form.setValue('locationNameTh', null);
      form.setValue('locationNameEn', null);
      form.setValue('buildingId', null);
      form.setValue('buildingName', null);
      setLocationMode('existing');
      setBuildingMode('existing');
      form.clearErrors([
        'locationId',
        'locationNameTh',
        'locationNameEn',
        'buildingId',
        'buildingName',
      ]);
    }
  }, [provinceId, form, initialValues?.provinceId]);

  // Location change -> reset building fields
  const prevLocationIdRef = useRef<string | undefined>();
  useEffect(() => {
    // Only clear building if in 'existing' mode
    if (locationMode !== 'existing') return;

    const currentLocationId = selectedLocation?.value as string | undefined;
    const prevLocationId = prevLocationIdRef.current;

    prevLocationIdRef.current = currentLocationId;

    // Skip on initial mount
    if (prevLocationId === undefined) {
      return;
    }

    // Skip if location is unchanged
    if (currentLocationId === prevLocationId) {
      return;
    }

    form.setValue('buildingId', null);
    form.setValue('buildingName', null);
    setBuildingMode('existing');
    form.clearErrors(['buildingId', 'buildingName']);
  }, [selectedLocation?.value, locationMode, form]);

  // Controlled submit
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const internalSubmitHandler = async (
    values: ExamLocationRequestFormState,
    options?: ExamLocationRequestFormSubmitOptions,
  ) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const parsed = ExamLocationRequestFormSchema.parse({
        provinceId: values.provinceId?.value ?? '',
        locationId: values.locationId?.value ?? null,
        locationNameTh: values.locationNameTh ?? null,
        locationNameEn: values.locationNameEn ?? null,
        buildingId: values.buildingId?.value ?? null,
        buildingName: values.buildingName ?? null,
      });
      await onSubmit(parsed, options);
    } finally {
      setSubmitting(false);
    }
  };

  // For form element submission (no options)
  const internalSubmit = async (values: ExamLocationRequestFormState) => {
    await internalSubmitHandler(values);
  };

  const submit = async (options?: ExamLocationRequestFormSubmitOptions) => {
    await form.handleSubmit((values) => internalSubmitHandler(values, options))();
  };

  const isUpdateMode = !!(
    initialValues &&
    Object.values(initialValues).some((v) => v !== undefined && v !== null && v !== '')
  );

  // Derived "canSubmit" based on current modes and required fields
  const canSubmit = useMemo(() => {
    if (submitting || loadingProvinces || loadingLocations || loadingBuildings) return false;

    const hasProvince = !!selectedProvince;
    if (!hasProvince) return false;

    if (locationMode === 'existing') {
      const hasLocation = !!selectedLocation;
      if (!hasLocation) return false;

      if (buildingMode === 'existing') {
        return !!buildingIdSelected;
      }
      if (buildingMode === 'new') {
        return !!(buildingName && buildingName.trim().length > 0);
      }
      return false;
    }

    if (locationMode === 'new') {
      const hasLocationNameTh = !!(locationNameTh && locationNameTh.trim().length > 0);
      const hasBuildingName = !!(buildingName && buildingName.trim().length > 0);
      return hasLocationNameTh && hasBuildingName;
    }

    return false;
  }, [
    submitting,
    loadingProvinces,
    loadingLocations,
    selectedProvince,
    locationMode,
    selectedLocation,
    buildingMode,
    buildingIdSelected,
    buildingName,
    locationNameTh,
    loadingBuildings,
  ]);

  const contextValue: InternalContextValue = {
    form,
    locationMode,
    setLocationMode: (val) => {
      setLocationMode(val);
      // Reset location related fields on mode change
      form.setValue('locationId', null);
      form.setValue('locationNameTh', null);
      form.setValue('locationNameEn', null);
      form.setValue('buildingId', null);
      form.setValue('buildingName', null);
      form.clearErrors([
        'locationId',
        'locationNameTh',
        'locationNameEn',
        'buildingId',
        'buildingName',
      ]);
      setBuildingMode('existing');
    },
    buildingMode,
    setBuildingMode: (val) => {
      setBuildingMode(val);
      form.setValue('buildingId', null);
      form.setValue('buildingName', null);
      form.clearErrors(['buildingId', 'buildingName']);
    },
    provinceOptions,
    locationOptions,
    buildingOptions,
    isProvinceSelected,
    isLocationSelected,
    submitting: submitting || loadingProvinces || loadingLocations || loadingBuildings,
    isUpdateMode,
    submit,
    canSubmit,
  };

  const content = (
    <ExamLocationRequestFormInternalContext.Provider value={contextValue}>
      <FormProvider {...form}>
        {withFormElement ? (
          <Form
            onSubmit={form.handleSubmit(internalSubmit)}
            sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
          >
            {children}
          </Form>
        ) : (
          children
        )}
        {hasUpdatePermission ? <FormLeavingListener onSubmit={internalSubmit} /> : null}
      </FormProvider>
    </ExamLocationRequestFormInternalContext.Provider>
  );

  return content;
}
